import theme from "../styles/theme";
import React, { useState, useRef, useEffect } from "react";
import { components } from "react-select";
import { useLocation, useNavigate } from "react-router-dom";

import "../styles-arabic/search-input.rtl.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";

import { searchAPI, languageAPI } from "../axios/search-axios";

import "react-simple-keyboard/build/css/index.css";

import { toast } from "react-toastify";
import { DEFAULT_MATCH_TYPE, normalizeSearchRequest } from "../utils/searchRequest";
import { getTranslatedSearchTerm } from "../utils/searchResponse";
import { trackSearch } from "../utils/searchAnalytics";

const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", short: "EN" },
  { code: "fr", label: "French", short: "FR" },
  { code: "es", label: "Spanish", short: "ES" },
  { code: "pt", label: "Portuguese", short: "PT" },
];

const DEFAULT_LANGUAGE_INITIALIZED_KEY = "searchLanguageDefaultsInitialized";
const TRANSLATION_USER_CONTROLLED_KEY = "enableTranslationUserControlled";
const DEFAULT_TRANSLATE_FROM =
  LANGUAGE_OPTIONS.find((option) => option.code === "en") || LANGUAGE_OPTIONS[0];
const DEFAULT_SEARCH_IN =
  { code: "ar", label: "Arabic", short: "AR" };

const useSearchInput = ({
  searchString,
  setSearchResults,
  setSearchResultSets,
  setCurrentPage,
  setAnalytics,
  setLoading,
  setSearchAfter,
  setTotalCount,
  setEffectiveSearchTerm: setParentEffectiveSearchTerm,
}) => {
  const location = useLocation();
  const filterStyles = {
    control: (provided, { isFocused }) => ({
      ...provided,
      backgroundColor: "transparent",
      border: "none",
      borderRadius: "8px",
      boxShadow: isFocused ? "0 0 0 2px #427e93" : "none",
      fontFamily: '"Roboto", sans-serif',
      fontSize: "16px",
      minHeight: "32px",
      cursor: "pointer",
      "&:hover": { backgroundColor: "#eef4f6" },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 4px 0 0",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "6px",
    }),
    placeholder: (base) => ({
      ...base,
      fontFamily: '"Roboto", sans-serif',
      fontSize: "16px",
      fontWeight: 400,
    }),
    singleValue: (base) => ({
      ...base,
      color: theme.colors.fontDarkBlue,
      fontFamily: '"Roboto", sans-serif',
      fontWeight: 500,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isSelected
        ? theme.colors.mainRed
        : isFocused
        ? theme.colors.mainGrayBackground
        : undefined,
      fontFamily: '"Roboto Bold", "Roboto", sans-serif',
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      boxShadow: "0 8px 24px rgba(18, 57, 77, 0.16)",
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const [searchInput, setSearchInput] = useState(
    localStorage.getItem("searchTerm")
      ? JSON.parse(localStorage.getItem("searchTerm"))
      : ""
  );
  const [isHovered, setIsHovered] = useState(false);
  const [matchType, setMatchType] = useState(() => {
    // Home starts a new search; results retain the researcher's chosen mode.
    if (location.pathname === "/home") return DEFAULT_MATCH_TYPE;
    const stored = localStorage.getItem("matchType")?.replace(/^"(.*)"$/, "$1");
    return ["any", "all", "exact"].includes(stored) ? stored : DEFAULT_MATCH_TYPE;
  });
  const getInitialEnableTranslation = () => {
    if (localStorage.getItem(TRANSLATION_USER_CONTROLLED_KEY) !== "true") {
      return false;
    }

    try {
      return JSON.parse(localStorage.getItem("enableTranslation")) === true;
    } catch {
      return false;
    }
  };

  const [enableTranslation, setEnableTranslation] = useState(
    getInitialEnableTranslation
  );
  const languageOptions = LANGUAGE_OPTIONS;

  const getInitialLanguage = (storageKey, options) => {
    if (localStorage.getItem(DEFAULT_LANGUAGE_INITIALIZED_KEY) !== "true") {
      return options[0];
    }

    try {
      const stored = localStorage.getItem(storageKey);
      const parsed = stored ? JSON.parse(stored) : options[0];
      return options.find((option) => option.code === parsed.code) || options[0];
    } catch {
      return options[0];
    }
  };

  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    getInitialLanguage("selectedLanguage", languageOptions)
  );

  const handleLanguageChange = async (lang) => {
    setSelectedLanguage(lang);
    localStorage.setItem("selectedLanguage", JSON.stringify(lang));
    try {
      await languageAPI.put("", { language: lang.code });
    } catch (error) {
      console.error("Failed to set language:", error);
    }
  };

  const handleEnableTranslationChange = (event) => {
    const checked = event.target.checked;
    setEnableTranslation(checked);
    localStorage.setItem(TRANSLATION_USER_CONTROLLED_KEY, "true");
    localStorage.setItem("enableTranslation", JSON.stringify(checked));
    if (!checked) {
      setEffectiveSearchTerm("");
      if (setParentEffectiveSearchTerm) {
        setParentEffectiveSearchTerm("");
      }
      localStorage.removeItem("effectiveSearchTerm");
    }
  };
  const [selectedTargetLanguage, setSelectedTargetLanguage] = useState(() =>
    DEFAULT_SEARCH_IN
  );

  const [keyboardVisibility, setkeyboardVisibility] = useState(false);
  const [layoutName, setLayoutName] = useState("default");
  const [effectiveSearchTerm, setEffectiveSearchTerm] = useState(
    localStorage.getItem("effectiveSearchTerm")
      ? JSON.parse(localStorage.getItem("effectiveSearchTerm"))
      : ""
  );

  const navigate = useNavigate();
  const keyboard = useRef();

  useEffect(() => {
    if (localStorage.getItem(DEFAULT_LANGUAGE_INITIALIZED_KEY) === "true") {
      return;
    }

    localStorage.setItem(
      "selectedLanguage",
      JSON.stringify(DEFAULT_TRANSLATE_FROM)
    );
    localStorage.setItem(
      "selectedTargetLanguage",
      JSON.stringify(DEFAULT_SEARCH_IN)
    );
    localStorage.setItem("enableTranslation", JSON.stringify(false));
    localStorage.setItem(DEFAULT_LANGUAGE_INITIALIZED_KEY, "true");
  }, []);

  useEffect(() => {
    setSelectedTargetLanguage(DEFAULT_SEARCH_IN);
    localStorage.setItem(
      "selectedTargetLanguage",
      JSON.stringify(DEFAULT_SEARCH_IN)
    );
  }, []);

  useEffect(() => {
    if (typeof searchInput === "string" && searchInput.trim() === "") {
      setEffectiveSearchTerm("");
      localStorage.removeItem("effectiveSearchTerm");
    }
  }, [searchInput]);

  const handleMatchTypeChange = (event) => {
    setMatchType(event.value);
    localStorage.setItem("matchType", JSON.stringify(event.value));
  };

  const lang = localStorage.getItem("lang");
  const buildNoResultsMessage = () => {
    if (lang === "ar") {
      return "لم يتم العثور على نتائج";
    }

    return "No results found";
  };

  const handleSearch = async () => {
    setLoading(true);
    //Send the search term to the backend if string is not empty

    if (searchInput === "") {
      setLoading(false);
      toast.warn("Please enter a search term", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
      });
      return;
    }
    let searchBackend = {
      search_term: searchInput,
      match_type: matchType,
      enable_translation: enableTranslation,
      language: enableTranslation ? selectedLanguage.code : undefined,
      target_language: enableTranslation ? DEFAULT_SEARCH_IN.code : undefined,
      sort_param: "date",
      sort_type: "asc",
    };
    let res;
    try {
      res = await searchAPI.put("", normalizeSearchRequest(searchBackend));
    } catch (error) {
      console.error("Search failed:", error);
      setLoading(false);
      toast.error("Search failed. Please try again.", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
      });
      return;
    }
    trackSearch(searchBackend.search_term);

    const effectiveSearchTerm = enableTranslation
      ? getTranslatedSearchTerm(res)
      : "";

    if (res.data.docs.length === 0) {
      setLoading(false);
      localStorage.setItem("searchTerm", JSON.stringify(searchInput));
      localStorage.setItem("matchType", JSON.stringify(matchType));
      localStorage.setItem(
        "enableTranslation",
        JSON.stringify(enableTranslation)
      );
      localStorage.setItem(
        "selectedLanguage",
        JSON.stringify(selectedLanguage)
      );
      localStorage.setItem(
        "selectedTargetLanguage",
        JSON.stringify(DEFAULT_SEARCH_IN)
      );
      localStorage.setItem(
        "effectiveSearchTerm",
        JSON.stringify(effectiveSearchTerm)
      );
      setEffectiveSearchTerm(effectiveSearchTerm);
      if (setParentEffectiveSearchTerm) {
        setParentEffectiveSearchTerm(effectiveSearchTerm);
      }
      if (setSearchResults) setSearchResults([]);
      if (setSearchResultSets) setSearchResultSets([]);
      if (setCurrentPage) setCurrentPage(1);
      if (setAnalytics) setAnalytics({});
      if (setSearchAfter) setSearchAfter(null);
      if (setTotalCount) setTotalCount(0);
      toast.warn(buildNoResultsMessage(), {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
      });
      return;
    }

    res.data?.docs?.forEach((doc) => {
      doc.is_download_blocked = res.data?.download_blocked_list?.includes(
        doc.coll_identifier
      );
    });

    try {
      localStorage.setItem("searchResults", JSON.stringify(res.data.docs));
    } catch (e) {
      if (e instanceof DOMException && e.name === "QuotaExceededError") {
        try {
          localStorage.removeItem("searchResults");
          localStorage.clear();
          // Set other items back to local storage
          localStorage.setItem("searchTerm", JSON.stringify(searchInput));
          localStorage.setItem("matchType", JSON.stringify(matchType));
          localStorage.setItem("searchResults", JSON.stringify(res.data.docs));
          // Success handling, possibly a success message or log
        } catch (e) {
          toast.error("Could not save search results. Please try again.", {});
          localStorage.clear();
        }
      } else {
        toast.error("An error occurred. Please try again.", {});
        localStorage.clear();
      }
    }

    localStorage.setItem("analytics", JSON.stringify(res.data.analytics));
    localStorage.setItem("searchItemsSet", JSON.stringify(false));
    localStorage.setItem("searchTerm", JSON.stringify(searchInput));
    localStorage.setItem("matchType", JSON.stringify(matchType));
    localStorage.setItem(
      "enableTranslation",
      JSON.stringify(enableTranslation)
    );
    localStorage.setItem("selectedLanguage", JSON.stringify(selectedLanguage));
    localStorage.setItem(
      "selectedTargetLanguage",
      JSON.stringify(DEFAULT_SEARCH_IN)
    );
    localStorage.setItem(
      "effectiveSearchTerm",
      JSON.stringify(effectiveSearchTerm)
    );
    setEffectiveSearchTerm(effectiveSearchTerm);
    if (setParentEffectiveSearchTerm) {
      setParentEffectiveSearchTerm(effectiveSearchTerm);
    }

    if (setSearchResults) {
      setSearchResults(res.data.docs);
      setAnalytics(res.data.analytics);
      setSearchResultSets((prevSearchResultSets) => ({
        [1]: res.data.docs,
      }));
    }

    if (setCurrentPage) {
      setCurrentPage(1);
    }

    navigate("/search-results", {
      state: {
        searchString: searchInput,
        searchResults: res.data.docs,
        analytics: res.data.analytics,
        analyticsQueryKey: res.data.analytics_query_key,
        totalCount: res.data.count,
        searchAfter: res.data.search_after,
        pageNumber: 1,
        effectiveSearchTerm,
        matchType,
        searchRequest: normalizeSearchRequest(searchBackend),
      },
    });
    setLoading(false);
  };

  library.add(faChevronDown);

  const filterOptions =
    lang === "ar"
      ? [
          {
            label: "أي كلمة",
            value: "any",
            description: "يعثر على نتائج تحتوي على كلمة واحدة على الأقل من الكلمات التي أدخلتها.",
            guidance: "استخدم هذا الخيار لأوسع نطاق بحث.",
            tooltipLanguage: "ar",
          },
          {
            label: "جميع الكلمات",
            value: "all",
            description: "يعثر على نتائج تحتوي على جميع الكلمات التي أدخلتها، بأي ترتيب.",
            guidance: "لا يلزم أن تظهر الكلمات بجوار بعضها.",
            tooltipLanguage: "ar",
          },
          {
            label: "العبارة الدقيقة",
            value: "exact",
            description: "يعثر على نتائج تظهر فيها جميع الكلمات معًا وبالترتيب نفسه الذي أدخلتها به.",
            guidance: "استخدم هذا الخيار للأسماء أو العناوين أو العبارات المحددة.",
            tooltipLanguage: "ar",
          },
        ]
      : [
          {
            label: "Any Word",
            value: "any",
            description: "Finds results containing at least one of the words you entered.",
            guidance: "Use this for the broadest search.",
          },
          {
            label: "All Words",
            value: "all",
            description: "Finds results containing all the words you entered, in any order.",
            guidance: "The words do not need to appear next to each other.",
          },
          {
            label: "Exact Phrase",
            value: "exact",
            description: "Finds results where all the words appear together in exactly the order you entered them.",
            guidance: "Use this for names, titles, or specific phrases.",
          },
        ];

  const Placeholder = (props) => {
    return <components.Placeholder {...props} />;
  };

  const CaretDownIcon = () => {
    return <FontAwesomeIcon icon="chevron-down" className="option-arrow" />;
  };

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <CaretDownIcon />
      </components.DropdownIndicator>
    );
  };

  // ----------------Arabic Keyboard----------------

  const toggleKeyboard = () => {
    setkeyboardVisibility(!keyboardVisibility);
  };

  const onKeyPress = (button) => {
    if (button === "{shift}" || button === "{lock}") handleShift();
    if (button === "{enter}") handleSearch();
    if (button === "{bksp}") setSearchInput(searchInput.slice(0, -1));
  };

  const handleShift = () => {
    setLayoutName((prevLayoutName) =>
      prevLayoutName === "default" ? "shift" : "default"
    );
  };

  const defaultValue = filterOptions.find((option) => option.value === matchType);

  const onChangeInput = async (event) => {
    const input = event.target.value;
    setSearchInput(input);
    // effectiveSearchTerm reflects the last *submitted* search's translation —
    // clear it so the "Effective search term" preview doesn't keep showing a
    // stale translation for text the user has since changed but not searched yet.
    setEffectiveSearchTerm("");
    if (setParentEffectiveSearchTerm) {
      setParentEffectiveSearchTerm("");
    }
    if (keyboard && keyboard.current) keyboard.current.setInput(input);
  };

  const onChangeArabicKeyboard = (input) => {
    setSearchInput(input);
    setEffectiveSearchTerm("");
    if (setParentEffectiveSearchTerm) {
      setParentEffectiveSearchTerm("");
    }
    if (keyboard && keyboard.current) keyboard.current.setInput(input);
  };

  return {
    searchInput,
    setSearchInput,
    isHovered,
    setIsHovered,
    matchType,
    setMatchType,
    setEnableTranslation,
    enableTranslation,
    handleEnableTranslationChange,
    keyboardVisibility,
    setkeyboardVisibility,
    layoutName,
    setLayoutName,
    filterStyles,
    filterOptions,
    effectiveSearchTerm,
    handleSearch,
    handleMatchTypeChange,
    onChangeInput,
    toggleKeyboard,
    onChangeArabicKeyboard,
    onKeyPress,
    handleShift,
    defaultValue,
    Placeholder,
    DropdownIndicator,
    keyboard,
    selectedLanguage,
    handleLanguageChange,
    languageOptions,
    selectedTargetLanguage,
  };
};

export default useSearchInput;
